import React, {useEffect, useMemo, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Dropdown, DropDownItem} from "../common/DropDown";
import {
    BookmakerData,
    BookmakerObject,
    EventsStoredObjects,
    EventStoredObject,
    LiveSettingsObject,
    User,
} from "../../../@types/response";
import {useDispatch, useSelector} from "react-redux";
import {getBookmakers} from "../../../store/selectors/getBookmakers";
import FilterInput from "../common/FilterInput";
import {getErrorMessage} from "../../../store/selectors/getErrorMessage";
import {AutocompleteLive} from "../common/Autocomplete";
import {getFixtures} from "../../../store/selectors/getFixtures";
import {ActiveEventIds, searchActiveFixtures} from "../../../store/utils/searchFixtures";
import {faTrophy} from "@fortawesome/free-solid-svg-icons/faTrophy";
import {getTemplate} from "../../../store/selectors/live/getTemplate";
import {faRetweet} from "@fortawesome/free-solid-svg-icons";
import Logo from "../logo";
import Separator from "../Separator";
import {SetSettingsAction} from "../../../store/actions/live/SetSettingsAction";
import {getSettings} from "../../../store/selectors/live/getSettings";
import {isInitialized} from "../../../store/selectors/live/isInitialized";
import {HeartBeatDropDown} from "../common/HeartBeatDropDown";
import {getComparisons} from "../../../store/selectors/getComparisons";
import {ComparisonsDropdown} from "./DropDown";
import getClient from "../../../store/selectors/getClient";
import {AppDispatch} from "recharts/types/state/store";
import {GetAlertsAction} from "../../../store/actions/live/GetAlertsAction";
import {getLiveBooks} from "../../../store/selectors/live/getBooks";
import {LiveEventsDrawer} from "./LiveEventsDrawer";
import {getEvents} from "../../../store/selectors/live/getEvents";

export default function Toolbar() {
    const allBookmakers: BookmakerData = useSelector(getBookmakers);
    const comparisons: BookmakerObject[] = useSelector(getComparisons);

    const template: null | string = useSelector(getTemplate);
    const fixtures: EventsStoredObjects = useSelector(getFixtures);
    const liveEvents: EventsStoredObjects = useSelector(getEvents);
    const errorMessage: string = useSelector(getErrorMessage);
    const storedSettings: LiveSettingsObject = useSelector(getSettings);
    const loading: boolean = useSelector(isInitialized);
    const client: User | undefined = useSelector(getClient);
    const books: number[] = useSelector(getLiveBooks)

    const [pendingSettings, setPendingSettings] = useState<boolean>(false);
    const [showCompetition, setShowCompetition] = useState<boolean>(false);
    const [settings, setSetting] = useState<LiveSettingsObject>(storedSettings);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const liveEventsButtonRef = useRef<HTMLButtonElement>(null);

    const activeLiveEventIds: ActiveEventIds = useMemo(
            () => new Set<number>(
                    Object.values(liveEvents)
                            .filter((event) => event?.status === 1)
                            .map((event) => event._id)
            ),
            [liveEvents]
    );

    const suggestions = useMemo(
            () => searchActiveFixtures(fixtures, activeLiveEventIds, searchQuery),
            [fixtures, activeLiveEventIds, searchQuery]
    );

    const searchableEvents: EventsStoredObjects = useMemo(
            () => Object.fromEntries(
                    Object.entries(liveEvents).map(([id, event]) => {
                        const fixture = fixtures[id];
                        const mergedEvent: EventStoredObject = {
                            _id: event._id ?? fixture?._id ?? parseInt(id, 10),
                            sportId: fixture?.sportId ?? event.sportId,
                            sport: fixture?.sport ?? event.sport,
                            categoryId: fixture?.categoryId ?? event.categoryId,
                            category: fixture?.category ?? event.category,
                            competitionId: fixture?.competitionId ?? event.competitionId,
                            competition: fixture?.competition ?? event.competition,
                            name: fixture?.name ?? event.name,
                            date: fixture?.date ?? event.date,
                            minute: event.minute ?? fixture?.minute,
                            score: event.score ?? fixture?.score,
                            status: event.status ?? fixture?.status,
                        };
                        return [id, mergedEvent];
                    })
            ),
            [fixtures, liveEvents]
    );

    const dispatch = useDispatch<AppDispatch>();

    const bookmakers: BookmakerData = useMemo(() => {
        return Object.keys(allBookmakers)
                .filter(id => books.includes(parseInt(id)) && (
                        client?.client == null || client.client === parseInt(id)
                ))
                .reduce((a, v) => ({...a, [v]: allBookmakers[v]}), {})
    }, [allBookmakers, client, books])

    useEffect(() => {
        setSetting(storedSettings);
    }, [storedSettings]);

    useEffect(() => {
        setPendingSettings(JSON.stringify(settings) !== JSON.stringify(storedSettings));
    }, [settings, storedSettings]);

    useEffect(() => {
        document.title = `${template ? template + ' - ' : ''}LIVE - OddMonitor`;
    }, [template]);

    return (
            <div className="flex w-full h-14 2xl:text-monitorBase text-monitorSm">
                <LiveEventsDrawer
                        isOpen={showCompetition}
                        onClose={() => setShowCompetition(false)}
                        toggleButtonRef={liveEventsButtonRef}
                />
                <Logo/>
                <ul className="flex items-center justify-around py-2 w-full">
                    <Separator/>
                    <li className="mx-4">
                        <AutocompleteLive
                                placeholder="search event"
                                searchAction={setSearchQuery}
                                suggestions={suggestions}
                                events={searchableEvents}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="from"
                                name="from"
                                label="FROM"
                                min={1}
                                max={50}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {from: parseFloat(e.target.value)}));
                                }}
                                value={settings.from}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="to"
                                name="to"
                                label="TO"
                                min={1}
                                max={100}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {to: parseFloat(e.target.value)}));
                                }}
                                value={settings.to}
                        />
                    </li>
                    <Separator/>
                    <li className="flex flex-col items-center">
                        <Dropdown label="BOOKMAKERS" color="default">
                            {Object.values(bookmakers)
                                    //TODO remove optional of name
                                    .sort((a, b) => b.name?.toLowerCase() > a.name?.toLowerCase() ? -1 : 1)
                                    .map((bookmaker) => {
                                        if (bookmaker.isComparison) return null;
                                        const visible = settings.bookmakers.includes(bookmaker.id);
                                        return (
                                                <DropDownItem
                                                        key={bookmaker.name}
                                                        label={bookmaker.name}
                                                        selected={visible}
                                                        onClick={() => {
                                                            if (!visible) {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: [...settings.bookmakers, bookmaker.id],
                                                                        })
                                                                );
                                                            } else {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: settings.bookmakers.filter(
                                                                                    (item) => item !== bookmaker.id
                                                                            ),
                                                                        })
                                                                );
                                                            }
                                                        }}
                                                />
                                        );
                                    })}
                        </Dropdown>
                    </li>
                    <Separator/>
                    <li className="flex flex-col items-center">
                        <ComparisonsDropdown
                                settings={settings}
                                comparisons={comparisons}
                                loading={loading}
                                setSetting={(settings) => {
                                    setSetting(settings)
                                }}
                        />
                    </li>
                    <Separator/>
                    <li>
                        <button
                                className={`p-3 rounded-md ${
                                        loading
                                                ? "bg-toolbar-settingsLoading text-white "
                                                : pendingSettings
                                                        ? "bg-toolbar-settingsWarning text-black "
                                                        : "bg-toolbar-settings text-white "
                                }`}
                                onClick={() => {
                                    dispatch(SetSettingsAction(settings));
                                    // dispatch(GetEventsAction({
                                    //     ...(settings.date && {date: settings.date}),
                                    //     ...(settings.bookmakers.length && {bookmakers: settings.bookmakers})
                                    // }));
                                    dispatch(GetAlertsAction())
                                }}
                        >
                            <FontAwesomeIcon className={loading ? "animate-spin" : ""} icon={faRetweet}/>
                        </button>
                    </li>
                    <li className="mx-4">
                        <button
                                className={"peer h-10 bg-toolbar-bgMain text-white mx-3"}
                                ref={liveEventsButtonRef}
                                onClick={() => setShowCompetition(!showCompetition)}
                        >
                            <FontAwesomeIcon icon={faTrophy}/>
                        </button>
                    </li>
                    <li className="mr-4">
                        <HeartBeatDropDown product="live"/>
                    </li>
                </ul>
                <div>{errorMessage}</div>
            </div>
    );
}
